import styled from 'styled-components';

import { HideContainer } from '@components/hide-container';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { hourList, minuteList } from '@utils/number-arrays';

import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { ACTION_TYPES } from 'ui/actions/action-config';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  DescriptionFormField,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
} from 'ui/journey-builder/node-types/common';

import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { getWaitForTimeLength } from '../common';

type WaitForData = {
  days: string;
  hours: string;
  minutes: string;
  description: string;
};

const ACTION_TYPE = ACTION_TYPES.wait_for_time;
const Form = createActionForm<WaitForData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const isDirty = formStore.useIsDirty();

  const days = (formStore.useFieldValue('days') ?? '') as string;
  const hours = (formStore.useFieldValue('hours') ?? '') as string;
  const minutes = (formStore.useFieldValue('minutes') ?? '') as string;
  const timeLengthText = getWaitForTimeLength(days, hours, minutes);

  const values = data?.values;
  const editMutation = useUpdateJourneyAction(journeyActionId);

  const handleSave = (data: WaitForData) =>
    editMutation.mutateAsync({
      action: ACTION_TYPE,
      isAutomatic: true,
      values: {
        days: { value: data.days },
        hours: { value: data.hours },
        minutes: { value: data.minutes },
      },
      description: data.description,
    });

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader actionType={ACTION_TYPE} {...props} />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
          <Stack space={6}>
            <HideContainer title={t('actionDetails')}>
              <DescriptionFormField defaultValue={data?.description} />
            </HideContainer>
            <Sidebar.Separator />
            <Stack space={4}>
              <Text text={t('selectHowLong')} font="regular14" />
              <Text text={t('useWhen')} font="regular14" />
            </Stack>
            <Separator />
            <FormGroupContainer>
              <Form.TextField
                id="days"
                label={t('days')}
                defaultValue={values?.days?.value ?? '0'}
                validators={['number']}
              />
              <Form.Select
                id="hours"
                label={t('hours')}
                options={hourList}
                placeholder=""
                defaultValue={values?.hours?.value ?? '0'}
                isClearable={false}
              />
              <Form.Select
                id="minutes"
                label={t('minutes')}
                options={minuteList}
                placeholder=""
                defaultValue={values?.minutes?.value ?? '0'}
                isClearable={false}
              />
            </FormGroupContainer>
            <Text
              text={timeLengthText.length > 0 ? t('waitsForDescription', { timeLengthText }) : ''}
            />
            <ActionFooter
              action={data}
              isDisabled={!isDirty || editMutation.isLoading}
              form={Form}
            />
          </Stack>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    actionDetails: 'Action details',
    selectHowLong: 'Select for how long journey should wait before triggering the following action',
    useWhen: 'Use when you want to pause by a defined amount of time between actions in a journey',
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
    waitsForDescription: 'Waits for {timeLengthText} before triggering the following action',
  },
};

export const FormGroupContainer = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
})({
  flexDirection: 'column',
  '> *': {
    flex: '1 1',
  },
  [theme.mediaBreakpoint.md]: {
    flexDirection: 'row',
  },
});
